import { Controller } from '@hotwired/stimulus';
import consumer from '../../channels/consumer';
import {
  embedSignature,
  onSignliftEvent,
  finalizeSignature,
  SIGNLIFT_EVENTS,
} from '../../lib/signlift';

const DOCLIFT_TIMEOUT = 180000;
const ERROR_DELAY = 5000;

export default class extends Controller {
  static targets = [
    'step2',
    'step3',
    'step4',
    'error',
    'contractLoadedButton',
    'stepTitle',
    'inProgressExplanations',
    'iframeLoadingExplanations',
    'readyExplanations',
  ];

  static values = {
    subscriptionId: String,
    titleInCaseOfDocReady: String,
    iframeTitle: String,
    signatureMounted: Boolean,
    finalizeInflight: Boolean,
  };

  connect() {
    if (!this.hasSubscriptionIdValue) return;

    this.docliftTimer = setTimeout(() => this.#giveUpOnDoclift(), DOCLIFT_TIMEOUT);

    this.channel = consumer.subscriptions.create(
      { channel: 'SignatureChannel', subscription_id: this.subscriptionIdValue },
      {
        received: (data) => this.#receive(data),
        rejected: () => this.#showErrorAfterDelay(),
      }
    );
  }

  disconnect() {
    clearTimeout(this.docliftTimer);
    clearTimeout(this.errorTimer);
    this.stopListeningToSignlift?.();
    this.channel?.unsubscribe();
  }

  showSignatureFrame() {
    this.step3Target.classList.remove('d-none');
    this.step2Target.classList.add('d-none');
    this.#announceSigning(true);
  }

  #announceSigning(signing) {
    this.dispatch('signing', { detail: { signing }, prefix: 'signature' });
  }

  #receive(data) {
    try {
      // Three states, not two: the signature can be acquired while Signlift is
      // still assembling the documents. Showing an error there would announce
      // the opposite of what just happened.
      if (data.status === 'awaiting_finalization') {
        this.#awaitFinalization();
        return;
      }

      if (data.status !== 'success') {
        this.#showErrorAfterDelay();
        return;
      }

      if (data.signing_url) {
        this.#mountSignature(data.signing_url, data.finalize_url);
        this.#announceDocumentReady();
      } else if (data.redirection_url) {
        window.location.href = data.redirection_url;
      }
    } catch {
      this.#showErrorAfterDelay();
    }
  }

  // The webhook or the nightly pass concludes it; nothing here can.
  #awaitFinalization() {
    clearTimeout(this.docliftTimer);
    this.signatureMountedValue = true;
    this.#advanceToStep4();
  }

  #mountSignature(signingUrl, finalizeUrl) {
    // A second broadcast would otherwise leave the first handler attached to
    // window for the life of the page, with no way left to remove it.
    this.stopListeningToSignlift?.();

    const iframe = embedSignature(this.step3Target, signingUrl);
    iframe.sandbox = 'allow-scripts allow-same-origin allow-forms';
    iframe.title = this.iframeTitleValue;
    // The signing page has no business knowing which subscription is open.
    iframe.referrerPolicy = 'no-referrer';

    // Listening in the same tick as the embed: the iframe cannot have loaded
    // and posted before the listener is on, and passing it lets the helper
    // refuse messages coming from any other window.
    this.stopListeningToSignlift = onSignliftEvent(
      ({ type }) => this.#handleSignliftEvent(type, finalizeUrl),
      signingUrl,
      iframe
    );
  }

  #handleSignliftEvent(type, finalizeUrl) {
    if (type === SIGNLIFT_EVENTS.READY) {
      this.#markSignatureReady();
    } else if (type === SIGNLIFT_EVENTS.SUCCESS) {
      if (this.finalizeInflightValue) return;
      this.finalizeInflightValue = true;

      // The screen moves on right away; the server confirms the signature
      // against the Signlift API on its side.
      this.#advanceToStep4();
      finalizeSignature(finalizeUrl);
    } else if (type === SIGNLIFT_EVENTS.ERROR) {
      this.#showError();
    }
  }

  // Signlift emits `ready` once its signing page is displayed and running, so
  // the button only opens on a frame the investor can actually use. Until then
  // signatureMounted stays false and the Doclift timer keeps watch.
  #markSignatureReady() {
    clearTimeout(this.docliftTimer);
    this.signatureMountedValue = true;

    this.contractLoadedButtonTargets.forEach((button) => {
      button.disabled = false;
    });

    if (this.hasIframeLoadingExplanationsTarget) {
      this.iframeLoadingExplanationsTarget.classList.add('d-none');
    }
    if (this.hasReadyExplanationsTarget) {
      this.readyExplanationsTarget.classList.remove('d-none');
    }
  }

  #announceDocumentReady() {
    if (this.hasStepTitleTarget) {
      this.stepTitleTarget.textContent = this.titleInCaseOfDocReadyValue;
    }
    if (this.hasInProgressExplanationsTarget) {
      this.inProgressExplanationsTarget.classList.add('d-none');
    }
    if (this.hasIframeLoadingExplanationsTarget) {
      this.iframeLoadingExplanationsTarget.classList.remove('d-none');
    }
  }

  #advanceToStep4() {
    this.step3Target.classList.add('d-none');
    this.step4Target.classList.remove('d-none');
    this.#announceSigning(false);
  }

  #giveUpOnDoclift() {
    if (this.signatureMountedValue) return;

    this.channel?.unsubscribe();
    this.#showError();
  }

  #showErrorAfterDelay() {
    this.errorTimer = setTimeout(() => this.#showError(), ERROR_DELAY);
  }

  #showError() {
    if (this.hasStep2Target) {
      this.step2Target.classList.add('d-none');
    }
    this.step3Target.classList.add('d-none');
    this.step4Target.classList.add('d-none');
    this.errorTarget.classList.remove('d-none');
    this.#announceSigning(false);
  }
}
